import React, { Fragment, useState, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import styled, { css } from 'styled-components';
import { Fade as ReactSlideFade } from 'react-slideshow-image';
import { withAnalytic } from '@skooldio/analytics-provider';

import { genSlideEvent, SLIDE_ACTIONS, getEventLabel } from 'Util/Analytics';
import { useClassroomContext } from 'Context/ClassroomContext/';
import { Flex, Box, Text, Image, SvgIcon } from 'Components/Base';
import { useWireSync } from 'Util/WireSync';
import { VideoBoxContainer } from 'Components/VideoPlayer/components';

import 'react-slideshow-image/dist/styles.css';

const SlideOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const SliderWrapper = styled(Box)`
  position: relative;
`;

const IconWrapper = styled(Text)`
  line-height: 1;
  cursor: pointer;
  height: auto;

  ${({ isVisible }) => css`
    visibility: ${isVisible ? 'hidden' : 'visible'};
  `}

  ${({ arrows, nextArrow }) =>
    arrows &&
    css`
      svg {
        transition: transform 0.3s ease-in-out;
      }

      &:hover {
        svg {
          transform: ${nextArrow ? `translateX(10%)` : `translateX(-10%)`};
        }
      }
    `}
`;

const ControlBarWrapper = styled(Flex)`
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.5));
  position: absolute;
  width: 100%;
  height: 20%;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
`;

const SyncButtonWrapper = styled(Flex)`
  cursor: pointer;
  svg {
    transition: transform 0.5s ease-in-out;
  }

  &:hover {
    svg {
      transform: rotate(180deg);
    }
  }
`;

const SyncText = styled((props) => <Text display={['none', 'block']} {...props} />)`
  &:hover {
    text-decoration: underline;
  }
`;

const PageMarkWrapper = styled(Flex)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const Slide = ({
  slides,
  syncKey,
  sliderValue,
  t,
  onLogAction,
  rightControlBarAdornment,
  ...props
}) => {
  const { role, value, setValue, reset, node, ROLES } = useWireSync(syncKey);
  const slickRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { courseData, currentSubsectionId } = useClassroomContext();

  /*
  SKOOLDIO-5416 : 20 and 40 are values of slider
  - slider value between 21 - 40, then video is a medium size.
  - slider value less than 20, then video is a small size.
  */
  const isMediumSlideSize = sliderValue <= 0.4;
  const isSmallSlideSize = sliderValue <= 0.2;

  const handleSetCurrentSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
    return slickRef.current?.goTo(slideIndex);
  };

  useEffect(() => {
    if (role === ROLES.SUBSCRIBER) {
      const lastIndex = slides.length - 1;
      const slideIndex = slides.findIndex((slide, index) => {
        const nextSlide = index !== lastIndex ? slides[index + 1] : {};
        return (
          (value >= slide?.time && value <= nextSlide?.time) ||
          (value >= slide?.time && index === lastIndex)
        );
      });
      if (currentIndex !== slideIndex) {
        handleSetCurrentSlide(slideIndex ?? 0);
      }
    }
  }, [currentIndex, role, slides, value]);

  const handlePrevSlide = () => {
    setValue(value, ROLES.OUTLIER);
    handleSetCurrentSlide(currentIndex - 1);
    onLogAction(
      genSlideEvent(SLIDE_ACTIONS.PREVIOUS_SLIDE, getEventLabel(courseData, currentSubsectionId))
    );
  };
  const handleNextSlide = () => {
    setValue(value, ROLES.OUTLIER);
    handleSetCurrentSlide(currentIndex + 1);
    onLogAction(
      genSlideEvent(SLIDE_ACTIONS.NEXT_SLIDE, getEventLabel(courseData, currentSubsectionId))
    );
  };

  const handleSyncSlide = () => {
    setValue(value, ROLES.SUBSCRIBER);
    onLogAction(
      genSlideEvent(SLIDE_ACTIONS.SYNC_WITH_MASTER, getEventLabel(courseData, currentSubsectionId))
    );
  };

  const properties = {
    arrows: false,
    autoplay: false,
    infinite: false,
    transitionDuration: 200,
  };

  return (
    <SliderWrapper width={1} height={'100%'}>
      <VideoBoxContainer>
        {({ active, setLayoutActive, toggleLayoutActive }) => {
          return (
            <Fragment>
              <ReactSlideFade ref={slickRef} {...properties}>
                {slides.map((slide, index) => (
                  <Flex className={'each-fade'} key={`slide-${index}`} justifyContent={'center'}>
                    <Image src={slide?.url} width={'100%'} height={'100%'} />
                  </Flex>
                ))}
              </ReactSlideFade>
              <SlideOverlay
                onMouseOver={() => setLayoutActive(1000)}
                onClick={() => toggleLayoutActive()}
              >
                {!isSmallSlideSize && (
                  <ControlBarWrapper
                    active={active}
                    justifyContent={'space-between'}
                    alignItems={'flex-end'}
                    px={3}
                    pb={3}
                  >
                    <Flex>
                      {role === ROLES.OUTLIER && (
                        <SyncButtonWrapper onClick={handleSyncSlide}>
                          <IconWrapper color={'white'} fontSize={3} isVisible={!active}>
                            <SvgIcon icon={SvgIcon.ICON_LIST.SYNC} />
                          </IconWrapper>
                          {!isMediumSlideSize && (
                            <SyncText color={'white'} fontSize={1} ml={2}>
                              {t('VIDEO_SLIDE.SYNC')}
                            </SyncText>
                          )}
                        </SyncButtonWrapper>
                      )}
                    </Flex>
                    <PageMarkWrapper justifyContent={'center'} alignItems={'flex-end'}>
                      <Flex>
                        <IconWrapper
                          color={'white'}
                          fontSize={3}
                          onClick={handlePrevSlide}
                          isVisible={currentIndex === 0 || !active}
                          arrows
                        >
                          <SvgIcon icon={SvgIcon.ICON_LIST.BACK} />
                        </IconWrapper>
                        <Text textStyle={'body2'} color={'white'} fontWeight={'normal'} px={2}>
                          {`${currentIndex + 1} of ${slides.length}`}
                        </Text>
                        <IconWrapper
                          color={'white'}
                          fontSize={3}
                          onClick={handleNextSlide}
                          isVisible={currentIndex === slides.length - 1 || !active}
                          arrows
                          nextArrow
                        >
                          <SvgIcon icon={SvgIcon.ICON_LIST.NEXT} />
                        </IconWrapper>
                      </Flex>
                    </PageMarkWrapper>
                    {rightControlBarAdornment &&
                      React.cloneElement(rightControlBarAdornment, {
                        style: { visibility: active ? 'visible' : 'hidden' },
                      })}
                  </ControlBarWrapper>
                )}
              </SlideOverlay>
            </Fragment>
          );
        }}
      </VideoBoxContainer>
    </SliderWrapper>
  );
};

Slide.propTypes = {
  slides: PropTypes.array,
  sliderValue: PropTypes.number,
  onLogAction: PropTypes.func,
};

export default compose(withTranslation('classroom'), withAnalytic)(Slide);
